import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import { radii } from "../../../theme/shape";

function isImageSource(icon) {
  return (
    typeof icon === "string" &&
    (icon.startsWith("http://") ||
      icon.startsWith("https://") ||
      icon.startsWith("/") ||
      icon.startsWith("data:image/") ||
      /\.(png|jpe?g|gif|svg|webp)(\?|#|$)/i.test(icon))
  );
}

function getInitials(title) {
  const words = String(title || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return "AP";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

function renderIconNode(iconNode) {
  if (!iconNode) return null;
  if (typeof iconNode === "function") {
    const Icon = iconNode;
    return <Icon />;
  }
  return iconNode;
}

function renderActionIcon(actionIcon) {
  if (!actionIcon) return null;
  if (typeof actionIcon === "function") {
    const Icon = actionIcon;
    return <Icon />;
  }
  return actionIcon;
}

export function ProductAppCardSkeleton({ minHeight = 168 }) {
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "border.default",
        borderRadius: radii.surface,
        minHeight,
        p: 2,
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          gap: 1.5,
          width: "100%",
        }}
      >
        <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: radii.base, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton width="64%" height={20} />
          <Skeleton width="48%" height={18} sx={{ mt: 0.25 }} />
        </Box>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <Skeleton width="92%" height={18} />
        <Skeleton width="74%" height={18} />
      </Box>

      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
          mt: 1.5,
        }}
      >
        <Box sx={{ height: 30 }} />
        <Skeleton variant="circular" width={30} height={30} />
      </Box>
    </Box>
  );
}

export default function ProductAppCard({
  actionActive = false,
  actionIcon,
  ariaLabel,
  disabled = false,
  disabledTooltip = "",
  icon,
  iconNode,
  iconSx,
  minHeight = 168,
  onClick,
  subtitle,
  sx,
  text,
  title,
}) {
  const imageIcon = isImageSource(icon);
  const [imageLoaded, setImageLoaded] = useState(!imageIcon);
  const [imageFailed, setImageFailed] = useState(false);
  const interactive = Boolean(!disabled && onClick);

  useEffect(() => {
    setImageLoaded(!imageIcon);
    setImageFailed(false);
  }, [icon, imageIcon]);

  const initials = useMemo(() => getInitials(title), [title]);

  const card = (
    <Box
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel || title}
      component={interactive ? "button" : "article"}
      type={interactive ? "button" : undefined}
      onClick={interactive ? onClick : undefined}
      sx={[
        (theme) => {
          const activeColor = theme.palette.mode === "dark"
            ? theme.palette.success.light
            : theme.palette.success.main;
          const activeBg = alpha(activeColor, theme.palette.mode === "dark" ? 0.24 : 0.18);
          const activeHoverBg = alpha(activeColor, theme.palette.mode === "dark" ? 0.32 : 0.24);

          return {
            appearance: "none",
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "border.default",
            borderRadius: radii.surface,
            color: "text.primary",
            cursor: disabled ? "not-allowed" : interactive ? "pointer" : "default",
            display: "flex",
            flexDirection: "column",
            minHeight,
            outline: 0,
            opacity: disabled ? 0.62 : 1,
            p: 2,
            position: "relative",
            textAlign: "left",
            transition: "border-color 160ms ease, background-color 160ms ease, transform 160ms ease",
            width: "100%",
            "&:hover": interactive ? {
              bgcolor: "background.paper",
              borderColor: "border.strong",
              transform: "translateY(-1px)",
              ".ProductAppCard-action": {
                bgcolor: actionActive ? activeHoverBg : "action.hover",
                color: actionActive ? activeColor : "text.primary",
              },
            } : undefined,
            "&:focus-visible": interactive ? {
              borderColor: "primary.main",
              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
            } : undefined,
            ".ProductAppCard-action": {
              bgcolor: actionActive ? activeBg : "transparent",
              color: actionActive ? activeColor : disabled ? "text.disabled" : "text.secondary",
            },
            ".MuiSvgIcon-root": {
              fontSize: 19,
            },
          };
        },
        sx,
      ]}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          gap: 1.5,
          minWidth: 0,
          width: "100%",
        }}
      >
        <Box
          sx={[
            {
              alignItems: "center",
              bgcolor: "surface.muted",
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: radii.base,
              display: "flex",
              flexShrink: 0,
              height: 52,
              justifyContent: "center",
              overflow: "hidden",
              position: "relative",
              width: 52,
            },
            iconSx,
          ]}
        >
          {iconNode ? (
            renderIconNode(iconNode)
          ) : imageIcon && !imageFailed ? (
            <>
              <Skeleton
                variant="rounded"
                width={30}
                height={30}
                sx={{
                  borderRadius: radii.sm,
                  opacity: imageLoaded ? 0 : 1,
                  position: "absolute",
                  transition: "opacity 160ms ease",
                }}
              />
              <Box
                component="img"
                src={icon}
                alt={title}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageFailed(true)}
                sx={{
                  display: "block",
                  height: "72%",
                  objectFit: "contain",
                  opacity: imageLoaded ? 1 : 0,
                  transition: "opacity 180ms ease",
                  width: "72%",
                }}
              />
            </>
          ) : (
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: 14,
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              {initials}
            </Typography>
          )}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              color: "text.primary",
              fontSize: 15,
              fontWeight: 800,
              lineHeight: "20px",
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              noWrap
              sx={{
                color: "text.secondary",
                fontSize: 12,
                fontWeight: 500,
                lineHeight: "18px",
                mt: 0.25,
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Typography
        sx={{
          color: "text.secondary",
          display: "-webkit-box",
          fontSize: 12,
          fontWeight: 400,
          lineHeight: "18px",
          mt: 1.5,
          overflow: "hidden",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
        }}
      >
        {text}
      </Typography>

      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
          mt: "auto",
          pt: 1.5,
          width: "100%",
        }}
      >
        <Box component="span" sx={{ height: 30 }} />
        {actionIcon ? (
          <Box
            aria-hidden="true"
            className="ProductAppCard-action"
            component="span"
            sx={{
              alignItems: "center",
              borderRadius: radii.circle,
              display: "inline-flex",
              height: 30,
              justifyContent: "center",
              transition: "background-color 160ms ease, color 160ms ease",
              width: 30,
            }}
          >
            {renderActionIcon(actionIcon)}
          </Box>
        ) : null}
      </Box>
    </Box>
  );

  if (!disabled || !disabledTooltip) {
    return card;
  }

  return (
    <Tooltip title={disabledTooltip} placement="top" arrow>
      <Box>{card}</Box>
    </Tooltip>
  );
}
